import { FetchResult } from "@apollo/client";
import { useCallback, useMemo } from "react";
import { FormattedMessage } from "react-intl";

import CircleButton from "@components/buttons/CircleButton";
import Fieldset from "@components/forms/Fieldset";
import styles from "@components/forms/SingleQuestionAutosave.module.css";
import BodyCopy from "@components/typography/BodyCopy";
import InputLabel from "@components/typography/InputLabel";
import { Opener } from "@hooks/useOpener";

type Props<T> = {
  currentSelections: T[];
  onSelect: (values: T[]) => Promise<FetchResult>;
  opener: Opener;
  options: {
    label: string;
    value: T;
  }[];
  questionKey: string;
};

const SingleQuestionAutosaveMultiselect = <T extends string>({
  currentSelections,
  onSelect,
  opener,
  options,
  questionKey
}: Props<T>) => {
  const visibleOptions = useMemo(() => {
    if (opener.isOpen) {
      return options;
    } else {
      return options.filter(option => currentSelections.includes(option.value));
    }
  }, [currentSelections, opener.isOpen, options]);

  const saveNewSelections = useCallback(
    (value: T) => {
      const newSelections = [...currentSelections];
      if (currentSelections.includes(value)) {
        newSelections.splice(newSelections.indexOf(value), 1);
      } else {
        newSelections.push(value);
      }
      onSelect(newSelections);
    },
    [currentSelections, onSelect]
  );

  const generateChangeHandler = useCallback(
    (value: T) => () => {
      saveNewSelections(value);
    },
    [saveNewSelections]
  );

  return (
    <Fieldset
      legend={
        <InputLabel labelPlacement="stacked">
          <FormattedMessage id={questionKey} />
        </InputLabel>
      }
    >
      <div className={styles.options}>
        {visibleOptions.map(option => {
          const id = `${questionKey}-${option.value}`;
          return (
            <div className={styles.optionWrapper} key={option.value}>
              <input
                checked={currentSelections.includes(option.value)}
                className={styles.invisible}
                id={id}
                name={option.value}
                onChange={generateChangeHandler(option.value)}
                type="checkbox"
              />
              <label
                className={`${styles.option} ${styles.multiselectLabel} ${
                  !currentSelections.includes(option.value) && styles.notChecked
                }`}
                htmlFor={id}
              >
                <BodyCopy color={currentSelections.includes(option.value) ? "primary-500" : "gray-300"} size="small">
                  {option.label}
                </BodyCopy>
              </label>
            </div>
          );
        })}
        <CircleButton
          ariaLabelKey="accessibility.ariaLabels.add"
          color={opener.isOpen ? "success" : "primary-500"}
          icon={opener.isOpen ? "checkmark" : "plus"}
          onClick={opener.toggle}
          size="small"
        />
      </div>
    </Fieldset>
  );
};

export default SingleQuestionAutosaveMultiselect;
